import { PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import {
  selectBaseApiUrl,
  selectCookieAuth,
  selectFirebaseApiKey,
  selectLoginUrl,
} from '../../core/config-loader/store/config-loader-slice';
import authAPI, { FirebaseLoginResponseDTO, logout } from '../http/authAPI';
import { LoginBackendResponseDTO } from '../http/dto/loginBackendResponseDto';
import { AuthState, authActions, selectAuthState } from './authSlice';

function* loginWithEmailAndPassword(action: PayloadAction<{ email: string; password: string; navigate: NavigateFunction }>) {
  try {
    const firebaseKey: string = yield select(selectFirebaseApiKey);
    const fireBaseResponse: AxiosResponse<FirebaseLoginResponseDTO> = yield call(() =>
      authAPI.logInToFirebaseWithEmailAndPassword(
        {
          returnSecureToken: true,
          password: action.payload.password,
          email: action.payload.email,
        },
        firebaseKey
      )
    );
    yield put(
      authActions.logInWithEmailAndPasswordSuccess({ firebaseLoginResponseDTO: fireBaseResponse.data, navigate: action.payload.navigate })
    );
  } catch (error: AxiosError | unknown) {
    Sentry.captureException(error);
    let errorMessage = error?.toString();
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data.error.message;
    }
    yield put(authActions.logInWithEmailAndPasswordError({ errors: { otherError: errorMessage } }));
  }
}

function* loginWithLocalId(action: PayloadAction<{ firebaseLoginResponseDTO: FirebaseLoginResponseDTO; navigate: NavigateFunction }>) {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    const loginUrl: string = yield select(selectLoginUrl);
    const backendResponse: AxiosResponse<LoginBackendResponseDTO> = yield call(() =>
      authAPI.logInToLeadtechBackendWithEmailAndLocalId(baseApiUrl, loginUrl)
    );

    yield put(authActions.logInWithLocalIdSuccess({ loginBackendResponseDTO: backendResponse.data, navigate: action.payload.navigate }));
  } catch (e) {
    Sentry.captureException(e);
    yield put(authActions.logInWithLocalIdError({ error: 'LOGIN.TEMP_ERROR' }));
  }
}

function* loginToFirebaseByOAuthWithTokenId(payload: PayloadAction<{ idToken: string; navigate: NavigateFunction }>) {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    const loginUrl: string = yield select(selectLoginUrl);

    const backendResponse: AxiosResponse<LoginBackendResponseDTO> = yield call(() =>
      authAPI.logInToLeadtechBackendWithEmailAndLocalId(baseApiUrl, loginUrl)
    );

    yield put(authActions.logInWithLocalIdSuccess({ loginBackendResponseDTO: backendResponse.data, navigate: payload.payload.navigate }));
  } catch (e) {
    Sentry.captureException(e);
    yield put(authActions.logInWithLocalIdError({ error: 'LOGIN.TEMP_ERROR' }));
  }
}

function* storeAuthStateAndNavigate(action: PayloadAction<{ navigate: NavigateFunction }>) {
  const authState: AuthState = yield select(selectAuthState);
  const isCookieAuth: boolean = yield select(selectCookieAuth);
  // In cookie-auth mode the session lives in an HttpOnly cookie, so tokens must never be
  // persisted to localStorage (XSS-reachable). Keep non-sensitive state for reload UX.
  const persistedState: AuthState = isCookieAuth
    ? { ...authState, idToken: '', refreshToken: '', localId: '', sessionId: undefined }
    : authState;
  localStorage.setItem('authState', JSON.stringify(persistedState));
  action.payload.navigate('/');
}

function* logoutSaga(action: PayloadAction<{ navigate: NavigateFunction; isUnauthorized?: boolean }>) {
  try {
    const baseApiUrl: string = yield select(selectBaseApiUrl);
    yield call(logout, baseApiUrl);

    localStorage.setItem('authState', '');
    action.payload.isUnauthorized ? action.payload.navigate('/login?unauthorized=true') : action.payload.navigate('/login');

    yield put(authActions.logoutSuccess());
  } catch (e) {
    Sentry.captureException(e);
    yield put(authActions.logoutError());
  }
}

function* authSaga() {
  yield takeLatest(authActions.logInWithEmailAndPassword.type, loginWithEmailAndPassword);
  yield takeLatest(authActions.logInWithEmailAndPasswordSuccess.type, loginWithLocalId);
  yield takeLatest(authActions.logInWithLocalIdSuccess.type, storeAuthStateAndNavigate);
  yield takeLatest(authActions.logout.type, logoutSaga);
  yield takeLatest(authActions.loginToFirebaseByOAuthSuccess.type, loginToFirebaseByOAuthWithTokenId);
}

export default authSaga;
